import { Download, Plus, ShieldCheck, Users } from 'lucide-react';
import { Count } from '../components/CountUp';
import { Button, Eyebrow, BandChip, PageLoader } from '../components/ui';
import { useStore } from '../store';
import { useOrganisations } from '../useOrganisations';
import { exportOrganisationsCsv } from '../exportCsv';
import { aud, audStr } from '../money';
import type { OrgSummary } from '../types';

// Budget-used bar colour by threshold (matches the design tokens).
const usedColor = (pct: number) => (pct >= 90 ? '#E5142B' : pct >= 75 ? '#C2A500' : '#00D700');

const STATUS: Record<string, { label: string; ink: string; dot: string }> = {
  active: { label: 'Active', ink: '#00A800', dot: '#00D700' },
  closed: { label: 'Closed', ink: '#75757F', dot: '#9A9AA4' },
  draft: { label: 'Draft', ink: '#C2A500', dot: '#E0C200' },
  archived: { label: 'Archived', ink: '#75757F', dot: '#9A9AA4' },
};

const COLS = '2fr 0.9fr 1fr 1.05fr 0.9fr 0.9fr 1.1fr';

export function Organisations() {
  const { organisations, loading, error } = useOrganisations();
  const openOrg = useStore((s) => s.openOrg);
  const openCampaignModal = useStore((s) => s.openCampaignModal);

  if (loading) return <PageLoader label="Loading organisations…" />;
  if (error) return <Message title={error} tone="error" />;
  if (organisations.length === 0)
    return <Message title="No organisations yet. Seed a campaign (see docs/operations.md) to populate this list." />;

  // Portfolio totals across every programme.
  const count = organisations.length;
  const activeCount = organisations.filter((o) => o.status === 'active').length;
  const closedCount = count - activeCount;
  const participants = organisations.reduce((a, o) => a + o.participantCount, 0);
  const complete = organisations.reduce((a, o) => a + o.completeCount, 0);
  const spend = organisations.reduce((a, o) => a + o.spentUsd, 0);
  const budget = organisations.reduce((a, o) => a + (o.budgetUsd ?? 0), 0);
  const usedPct = budget > 0 ? Math.round((spend / budget) * 100) : 0;

  const rows = [...organisations].sort((a, b) => b.spentUsd - a.spentUsd);

  return (
    <div className="bg-subtle text-code" style={{ flex: 1 }}>
      <div className="fui-admin" style={{ maxWidth: 1180, margin: '0 auto', padding: '34px 32px 80px', fontVariantNumeric: 'tabular-nums' }}>
        {/* header */}
        <div className="animate-fadeUp" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap', marginBottom: 14 }}>
          <div>
            <span className="text-green-text uppercase tracking-eyebrow" style={{ fontSize: 11, fontWeight: 700 }}>Organisations</span>
            <h1 style={{ fontSize: 32, fontWeight: 700, letterSpacing: '-0.03em', margin: '10px 0 0' }}>Assessment programmes</h1>
            <div className="text-storm" style={{ fontSize: 14, marginTop: 7 }}>
              Every organisation running FluentAI, including Versent. Select one to open its dashboard.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Button variant="ghost" onClick={() => exportOrganisationsCsv(organisations)}>
              <Download size={16} /> Export summary
            </Button>
            <Button onClick={() => openCampaignModal('create')}>
              <Plus size={16} /> New organisation
            </Button>
          </div>
        </div>

        {/* governance bar */}
        <div className="animate-fadeUp bg-white border border-panel" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap', borderRadius: 8, padding: '11px 16px', marginBottom: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ShieldCheck size={17} className="text-green shrink-0" />
            <span className="text-storm" style={{ fontSize: 13 }}>
              Anonymised, role-level insights only. Spend is modelled from usage at list pricing; invoiced actuals reconcile monthly.
            </span>
          </div>
          <span className="text-storm border border-panel" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 11.5, fontWeight: 600, borderRadius: 999, padding: '5px 12px', flex: 'none' }}>
            <Users size={14} className="text-green" /> Viewing as Versent admin
          </span>
        </div>

        {/* summary KPI row */}
        <div className="fui-kpi animate-fadeUp" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 14 }}>
          <Kpi label="Organisations" note={`${activeCount} active · ${closedCount} closed`}>
            <Count end={count} />
          </Kpi>
          <Kpi label="Participants assessed" note="Completed across all programmes">
            <Count end={complete} />
            <span className="text-storm" style={{ fontSize: 22 }}> / {participants}</span>
          </Kpi>
          <Kpi label="Total spend" note={`Of ${audStr(budget)} allocated`}>
            <Money usd={spend} />
          </Kpi>
          <Kpi label="Budget used" note="">
            <Count end={usedPct} />
            <span className="text-storm" style={{ fontSize: 22 }}>%</span>
            <div className="bg-divider" style={{ height: 6, borderRadius: 999, overflow: 'hidden', marginTop: 10 }}>
              <div style={{ height: '100%', width: `${Math.min(100, usedPct)}%`, background: '#00D700', borderRadius: 999 }} />
            </div>
          </Kpi>
        </div>

        {/* organisations table */}
        <div className="animate-fadeUp bg-white border border-panel rounded-card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '20px 24px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <Eyebrow>All organisations</Eyebrow>
              <div className="text-storm" style={{ fontSize: 13, marginTop: 3 }}>Select a row to open the full dashboard</div>
            </div>
            <span className="text-storm border border-panel" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 11.5, fontWeight: 600, borderRadius: 999, padding: '5px 12px' }}>
              Sorted by spend
            </span>
          </div>

          <div className="fui-rosterhead bg-faint text-storm" style={{ display: 'grid', gridTemplateColumns: COLS, gap: 0, borderTop: '1px solid #EFEFF2', borderBottom: '1px solid #EFEFF2', padding: '11px 24px', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            <div>Organisation</div>
            <div>Status</div>
            <div>Participants</div>
            <div>Avg readiness</div>
            <div style={{ textAlign: 'right' }}>Spend</div>
            <div style={{ textAlign: 'right' }}>Budget</div>
            <div>Used</div>
          </div>

          {rows.map((o) => (
            <OrgRow key={o.campaignId} o={o} onOpen={() => openOrg(o.campaignId)} />
          ))}
        </div>
      </div>
    </div>
  );
}

function OrgRow({ o, onOpen }: { o: OrgSummary; onOpen: () => void }) {
  const st = STATUS[o.status] ?? STATUS.active;
  const used = o.budgetUsd && o.budgetUsd > 0 ? Math.round((o.spentUsd / o.budgetUsd) * 100) : 0;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="fui-rosterrow hover:bg-faint"
      style={{ display: 'grid', gridTemplateColumns: COLS, gap: 0, width: '100%', textAlign: 'left', background: 'transparent', border: 'none', borderBottom: '1px solid #EFEFF2', padding: '15px 24px', alignItems: 'center', cursor: 'pointer', font: 'inherit', transition: 'background .12s' }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
          {o.name}
          {o.internal && (
            <span className="text-green-text" style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', border: '1px solid #C8E6C8', background: 'rgba(0,215,0,0.07)', padding: '2px 7px', borderRadius: 4 }}>
              Internal
            </span>
          )}
        </div>
        {o.sector && <div className="text-storm" style={{ fontSize: 12, marginTop: 3 }}>{o.sector}</div>}
      </div>
      <div>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: st.ink }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: st.dot }} />
          {st.label}
        </span>
      </div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{o.completeCount} / {o.participantCount}</div>
        <div className="text-storm" style={{ fontSize: 11.5, marginTop: 2 }}>{o.completionPct}% complete</div>
      </div>
      <div><BandChip band={o.avgReadiness.band} label={o.avgReadiness.label} /></div>
      <div style={{ fontSize: 14, fontWeight: 700, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{audStr(o.spentUsd)}</div>
      <div className="text-storm" style={{ fontSize: 13.5, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        {o.budgetUsd != null ? audStr(o.budgetUsd) : '—'}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <div className="bg-divider" style={{ flex: 1, height: 6, borderRadius: 999, overflow: 'hidden', minWidth: 38 }}>
          <div style={{ height: '100%', width: `${Math.min(100, used)}%`, borderRadius: 999, background: usedColor(used) }} />
        </div>
        <span className="text-storm" style={{ fontSize: 12, fontWeight: 700, fontVariantNumeric: 'tabular-nums', minWidth: 30, textAlign: 'right' }}>{used}%</span>
      </div>
    </button>
  );
}

function Kpi({ label, note, children }: { label: string; note: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-panel rounded-card" style={{ padding: 20 }}>
      <Eyebrow>{label}</Eyebrow>
      <div style={{ fontSize: 40, fontWeight: 800, letterSpacing: '-0.03em', margin: '6px 0 10px', fontVariantNumeric: 'tabular-nums' }}>{children}</div>
      {note && <div className="text-storm" style={{ fontSize: 12 }}>{note}</div>}
    </div>
  );
}

function Money({ usd }: { usd: number }) {
  return (
    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
      <span className="text-storm" style={{ fontSize: 22 }}>A$</span>
      <Count end={aud(usd)} decimals={2} />
    </span>
  );
}

function Message({ title, tone = 'info' }: { title: string; tone?: 'info' | 'error' }) {
  return (
    <div className="bg-subtle text-code" style={{ flex: 1 }}>
      <div className="fui-admin" style={{ maxWidth: 1180, margin: '0 auto', padding: '34px 32px 80px' }}>
        <div className={`bg-white rounded-card border ${tone === 'error' ? 'border-critical' : 'border-panel'}`} style={{ padding: 24, fontSize: 14, color: tone === 'error' ? '#E5142B' : '#75757F' }}>
          {title}
        </div>
      </div>
    </div>
  );
}
